import { Injectable } from '@angular/core';
import { AccountDepartment } from 'src/app/types/AccountDepartment';
import { CompanyRegistrationType } from 'src/app/types/CompanyRegistrationType';
import { DocumentDetails } from 'src/app/types/DocumentDetails';
import { State } from 'src/app/types/States';
import { environment } from '../../../../environments/environment';
import {
  Address,
  BusinessActivityType,
  BusinessCredibilityInformationData,
  CompanyType,
  CountryIsoCode,
  CreateCustomerRequestContent,
  CreatePartnerRequestContent,
  CustomerStatus,
  CustomerType,
  DirectorDetails,
  GetCustomersData,
  IdentityVerificationQuery,
  IdentityVerificationSummary,
  IndiaBasicBusinessInformationData,
  IndiaBasicBusinessInformationNonEditableEnum,
  IndiaBusinessBankDetailsData,
  IndiaBusinessComplianceQuestionsData,
  IndiaBusinessDirectorDetailsData,
  IndiaBusinessRegistrationType,
  PartnerAccountDetails,
  PatchVerificationStepDataRequestContent,
  PatchVerificationStepDataResponseModel,
  PostIdentityVerificationRequestContent,
  PostIdentityVerificationResponseDataModel,
  SupportDocument,
  Term,
  UpdatePartnerRequestContent,
  UsBasicBusinessInformationData,
  UsBusinessComplianceQuestionsData,
  VerificationStep,
  VerificationStepName,
  VerificationType,
} from '../../../api/autogenerated';
import { mapVerificationStatus } from '../../../pages/admin/admin-kyb-verifications/admin-status.util';
import {
  OnboardingBusinessDetail,
  OnboardingQueueItem,
  OnboardingVerificationQuery,
  SubmitIdentityVerificationQueryDocumentRequest,
  SubmitIdentityVerificationQueryDocumentResponse,
} from '../../../pages/admin/admin-kyb-verifications/admin.types';
import {
  IndianAddress,
  Address as TAddress,
  USAddress,
} from '../../../types/Address';
import { AuthorizedOfficer } from '../../../types/AuthorizedOfficer';
import { AuthorizedOwner } from '../../../types/AuthorizedOwner';
import {
  Business,
  BusinessSearchNonEditablesEnum,
} from '../../../types/Business';
import { BusinessActivity } from '../../../types/BusinessActivity';
import { BusinessRegistrationType } from '../../../types/BusinessRegistrationType';
import { Country } from '../../../types/Country';
import { FlowSteps } from '../../../types/FlowSteps';
import { IdentityVerificationStepErrors } from '../../../types/IdentityVerificationStepErrors';
import { InviteBusinessRequest } from '../../../types/InviteBusinessRequest';
import { Profile } from '../../../types/Profile';
import { Responsibility } from '../../../types/Responsibility';
import { Role } from '../../../types/Role';
import { Step } from '../../../types/Step';
import { AdditionalInfoRequest } from '../../../types/requests/AdditionalInfoRequest';
import { BankAccountDetailsInfoRequest } from '../../../types/requests/BankAccountDetailsInfoRequest';
import { BusinessResponsibilitiesRequest } from '../../../types/requests/BusinessResponsibilitiesRequest';
import { BusinessSearchRequest } from '../../../types/requests/BusinessSearchRequest';
import { CreateBusinessRequest } from '../../../types/requests/CreateBusinessRequest';
import { BusinessOnboardingResponse } from '../../../types/responses/BusinessOnboardingResponse';
import { SharedProfileData } from '../../data/shared-profile-data';
import { IBusinessService } from '../IBusinessService';
import {
  mockClientCountries,
  mockCountries,
  mockUSStates,
} from '../mock/mock-data';
import { OpenApiService } from '../open-api.service';

@Injectable({
  providedIn: 'root',
})
export class KycOnboardingService implements IBusinessService {
  options = {
    headers: {
      'X-Organization-ID': environment.organization_id,
      customer_id: '',
    },
  };
  verification_id: string;
  registration_type: string;
  private identityVerificationSummariesCache: IdentityVerificationSummary[] =
    [];

  constructor(
    private openapiService: OpenApiService,
    private sharedProfileData: SharedProfileData,
  ) {}

  private mapperForFrontendToApiBusinessRegistrationType(): Record<
    BusinessRegistrationType,
    IndiaBusinessRegistrationType
  > {
    return Object.fromEntries(
      Object.entries(BusinessRegistrationType).map(([key, value]) => {
        const indiaType = key
          .replace(/([A-Z])/g, '_$1')
          .toUpperCase()
          .replace(/^_/, '');
        return [value, indiaType];
      }),
    ) as Record<BusinessRegistrationType, IndiaBusinessRegistrationType>;
  }

  private mapperForApiToFrontendBusinessRegistrationType(): Record<
    IndiaBusinessRegistrationType,
    BusinessRegistrationType
  > {
    const indiaEnum = Object.keys(BusinessRegistrationType).map((key) =>
      key.replace(/([a-z])([A-Z])/g, '$1_$2').toUpperCase(),
    );

    const businessEnum = Object.values(BusinessRegistrationType);

    const mapper: Record<
      IndiaBusinessRegistrationType,
      BusinessRegistrationType
    > = {} as any;

    indiaEnum.forEach((indiaType, index) => {
      mapper[indiaType as IndiaBusinessRegistrationType] = businessEnum[index];
    });

    return mapper;
  }

  private mapperForFrontendToApiBusinessActivity(): Record<
    BusinessActivity,
    BusinessActivityType
  > {
    return Object.fromEntries(
      Object.entries(BusinessActivity).map(([key, value]) => {
        const indiaType = key
          .replace(/([A-Z])/g, '_$1')
          .toUpperCase()
          .replace(/^_/, '');
        return [value, indiaType];
      }),
    ) as Record<BusinessActivity, BusinessActivityType>;
  }

  private mapperForApiToFrontendBusinessActivity(): Record<
    BusinessActivityType,
    BusinessActivity
  > {
    const indiaEnum = Object.keys(BusinessActivity).map((key) =>
      key.replace(/([a-z])([A-Z])/g, '$1_$2').toUpperCase(),
    );

    const businessEnum = Object.values(BusinessActivity);

    const mapper: Record<BusinessActivityType, BusinessActivity> = {} as any;

    indiaEnum.forEach((indiaType, index) => {
      mapper[indiaType as BusinessActivityType] = businessEnum[index];
    });

    return mapper;
  }

  private mapperForFrontendToApiCompanyRegistrationType(): Record<
    CompanyRegistrationType,
    CompanyType
  > {
    const mapper: Record<CompanyRegistrationType, CompanyType> = {
      [CompanyRegistrationType.Company]: 'COMPANY',
      [CompanyRegistrationType.Individual]: 'INDIVIDUAL',
      [CompanyRegistrationType.Other]: 'OTHER',
    };
    return mapper;
  }

  private async getAllCustomers(
    countryIsoCode: CountryIsoCode,
    businessRegTypeForRequest: IndiaBusinessRegistrationType,
  ) {
    let customerRequestContent: GetCustomersData['query'] = {
      country_code: countryIsoCode,
      customer_type: 'BUSINESS',
      mine: true,
      business_registration_type: businessRegTypeForRequest,
    };

    let responseFromGetCustomers = await this.openapiService.getCustomers(
      customerRequestContent,
    );
    return responseFromGetCustomers.data;
  }

  async checkIfUserBecameActive(
    customerId: string,
    selectedCountryCode: string,
  ) {
    let countryIsoCode: CountryIsoCode = 'IN';
    if (selectedCountryCode == 'US') {
      countryIsoCode = 'US';
    }

    let responseFromGetCustomerById = await this.openapiService.getCustomerById(
      { id: customerId },
    );
    console.log(responseFromGetCustomerById);

    const foundCustomer = responseFromGetCustomerById.data.status === 'ACTIVE';
    if (foundCustomer) {
      return true;
    } else {
      return false;
    }
  }

  async getDataPostFirstStep(
    businessRegType: string,
    selectedCountryCode: string,
  ) {
    // if (process.env.STORYBOOK == true) {
    //
    // }

    let countryIsoCode: CountryIsoCode = 'IN';
    if (selectedCountryCode == 'US') {
      countryIsoCode = 'US';
    }

    let mapperForFrontendToApiBusinessRegistrationType =
      this.mapperForFrontendToApiBusinessRegistrationType();
    let businessRegTypeForRequest: IndiaBusinessRegistrationType =
      mapperForFrontendToApiBusinessRegistrationType[
        businessRegType as BusinessRegistrationType
      ] as IndiaBusinessRegistrationType;

    let currentCustomersList = await this.getAllCustomers(
      countryIsoCode,
      businessRegTypeForRequest,
    );

    let currentInactiveCustomers = currentCustomersList.filter(({ status }) => {
      return status === 'INACTIVE';
    });

    let customerId: string;

    if (currentInactiveCustomers.length > 0 && currentInactiveCustomers[0].id) {
      customerId = currentInactiveCustomers[0].id;
    } else {
      // create customers details if doesn't exist,
      let customerRequestContent: CreateCustomerRequestContent = {
        status: 'INACTIVE',
        country_code: countryIsoCode,
        customer_type: 'BUSINESS',
        business_registration_type: businessRegTypeForRequest,
      };

      let customerCreationApiResponse =
        await this.openapiService.createCustomer(customerRequestContent);

      if (customerCreationApiResponse.data.id == undefined) {
        throw new Error('Api Response Failed');
      }
      customerId = customerCreationApiResponse.data.id;
    }

    // updates as default profile
    await this.openapiService.patchCustomer({
      operation: 'UPDATEASDEFAULT',
      id: customerId,
    });

    console.log(customerId);
    let type: VerificationType = 'KYB';

    let postIdentityVerificationRequestContent: PostIdentityVerificationRequestContent =
      {
        business_registration_type: businessRegTypeForRequest,
        country_iso_code: countryIsoCode,
        customer_id: customerId,
        type: type,
      };

    let response = await this.openapiService.postIdentityVerification(
      postIdentityVerificationRequestContent,
    );

    if (response.data.steps === undefined) {
      throw new Error('API RESPONSE ERROR');
    }

    let postIdentityVerificationResponseDataModel: PostIdentityVerificationResponseDataModel =
      response.data;
    if (
      postIdentityVerificationResponseDataModel.status !== 'PENDING' &&
      postIdentityVerificationResponseDataModel.status !== 'IN_PROGRESS' &&
      postIdentityVerificationResponseDataModel.status !== 'UNDER_REVIEW'
    ) {
      // @todo take another action
      throw new Error('Some error happened');
    }

    this.verification_id =
      postIdentityVerificationResponseDataModel.verification_id;
    this.registration_type = businessRegType;

    return this.getBusinessOnboardingResponse(
      postIdentityVerificationResponseDataModel,
    );
  }
  async getCountries(): Promise<Country[]> {
    return Promise.resolve(mockCountries);
  }

  async getClientCountries(): Promise<Country[]> {
    return Promise.resolve(mockClientCountries);
  }

  async getStates(): Promise<State[]> {
    return Promise.resolve(mockUSStates);
  }

  private async findBusinessesForPendingKyc(
    request: BusinessSearchRequest,
  ): Promise<Business[]> {
    let stepId: VerificationStepName = 'IN_BASIC_BUSINESS_INFORMATION';
    let patchVerificationStepDataRequestContent: PatchVerificationStepDataRequestContent =
      {
        operation: 'SEARCHFORPANORCIN',
        step_data: {
          india_basic_business_information: {
            pan: request.searchKey,
          },
        },
      };

    let dataFromResponse = await this.openapiService.patchVerificationStepData(
      this.verification_id,
      stepId,
      patchVerificationStepDataRequestContent,
    );

    let businessObj: Business = {} as Business;
    if (dataFromResponse.data.steps == undefined) {
      return [businessObj];
    }

    dataFromResponse.data.steps.forEach((step, index) => {
      if (step.verification_step_data == undefined) {
        return;
      }
      if ('india_basic_business_information' in step.verification_step_data) {
        if (
          step.verification_step_data.india_basic_business_information
            .error_code
        ) {
          return;
        }
        businessObj.PAN =
          step.verification_step_data.india_basic_business_information.pan;
        businessObj.CIN =
          step.verification_step_data.india_basic_business_information.cin;
        businessObj.name =
          step.verification_step_data.india_basic_business_information
            .business_name ?? businessObj.name;
        businessObj.authorizedOwnerName =
          step.verification_step_data.india_basic_business_information
            .authorized_person_name ?? businessObj.authorizedOwnerName;
        businessObj.nonEditables =
          this.getApiToFrontendIndiaBasicBusinessNonEditableFields(
            step.verification_step_data.india_basic_business_information,
          );
        if (
          step.verification_step_data.india_basic_business_information
            .address != undefined
        ) {
          businessObj.address = {} as TAddress;
          businessObj.address.street =
            step.verification_step_data.india_basic_business_information.address.address_line1;
          if (
            step.verification_step_data.india_basic_business_information.address
              .address_line2 != undefined
          ) {
            businessObj.address.street +=
              ', ' +
              step.verification_step_data.india_basic_business_information
                .address.address_line2;
          }
          businessObj.address.city =
            step.verification_step_data.india_basic_business_information.address.city;
          businessObj.address.state =
            step.verification_step_data.india_basic_business_information.address.state;
          businessObj.address.country =
            step.verification_step_data.india_basic_business_information.address.country_code;
          businessObj.address.specificDetails = {
            stateName:
              step.verification_step_data.india_basic_business_information
                .address.state,
            pinCode:
              step.verification_step_data.india_basic_business_information
                .address.pin_code,
          } as IndianAddress;
        }
      }
    });

    return [businessObj];
  }

  private async findBusinessesForVendorSearch(
    request: BusinessSearchRequest,
  ): Promise<Business[]> {
    return [{} as Business];
  }

  private async findBusinessesForClientSearch(
    request: BusinessSearchRequest,
  ): Promise<Business[]> {
    let customerRequestContent = {
      status: 'ACTIVE' as CustomerStatus,
      country_code: 'US' as CountryIsoCode,
      customer_type: 'BUSINESS' as CustomerType,
      mine: false,
      business_name: request.searchKey,
    };

    let responseFromGetCustomers = await this.openapiService.getCustomers(
      customerRequestContent,
    );
    const getCustomersList = responseFromGetCustomers.data;
    let businessObj: Business = {} as Business;

    if (getCustomersList.length > 0 && getCustomersList[0].id) {
      businessObj.r2AId = getCustomersList[0].id;
      return [businessObj];
    } else {
      return [];
    }
  }

  async findBusinesses(request: BusinessSearchRequest): Promise<Business[]> {
    let businesses = [{} as Business];
    try {
      switch (request.requestType) {
        case 'kyc': {
          businesses = await this.findBusinessesForPendingKyc(request);
          break;
        }
        case 'vendor': {
          businesses = await this.findBusinessesForVendorSearch(request);
          break;
        }
        case 'client': {
          businesses = await this.findBusinessesForClientSearch(request);
          break;
        }
        default: {
          break;
        }
      }
    } catch (error: any) {
      console.error('Error during business search:', error);
      throw error;
    }
    return businesses;
  }

  // todo seperate file
  private getAddress(address: Address): TAddress {
    let addressAss = {} as TAddress;
    addressAss.street = address.address_line1;
    addressAss.city = address.city;
    addressAss.state = address.state;
    addressAss.country = address.country_code;
    if (addressAss.country === 'IN') {
      addressAss.specificDetails = {
        stateName: address.state,
        pinCode: address.pin_code,
      } as IndianAddress;
    } else if (addressAss.country === 'US') {
      addressAss.specificDetails = {
        stateCode: address.state,
        zipCode: address.pin_code,
      } as USAddress;
    }
    return addressAss;
  }

  private getAuthorizedOwners(directors: DirectorDetails[]): AuthorizedOwner[] {
    let directorsList = [] as AuthorizedOwner[];
    directors.forEach((director) => {
      const authorizedOwner = {} as AuthorizedOwner;
      authorizedOwner.id = director.id ? director.id : '';
      authorizedOwner.name = '';
      authorizedOwner.roles = [];
      if (director.name) {
        authorizedOwner.name = director.name;
      }
      authorizedOwner.PAN = '';
      if (director.pan) {
        authorizedOwner.PAN = director.pan;
      }
      authorizedOwner.ubo = false;
      if (director.ubo) {
        authorizedOwner.ubo = director.ubo;
        authorizedOwner.roles.push(Role.UBO);
      }
      authorizedOwner.srManager = false;
      if (director.senior_management) {
        authorizedOwner.srManager = director.senior_management;
        authorizedOwner.roles.push(Role.SrManager);
      }
      authorizedOwner.isManual = director.is_manual;
      authorizedOwner.updateEnabled = director.update_enabled;
      if (
        director.aadhar_verification_details &&
        director.aadhar_verification_details.aadhar_number
      ) {
        authorizedOwner.aadharId =
          director.aadhar_verification_details.aadhar_number;
      }
      directorsList.push(authorizedOwner);
    });
    return directorsList;
  }

  private getBusinessDocumentsFromSupportDocuments(
    documents: SupportDocument[] = [],
  ): DocumentDetails[] {
    let documentDetails = [] as DocumentDetails[];
    documents.forEach((document) => {
      let documentDetail = {} as DocumentDetails;
      documentDetail.name = document.name;
      documentDetail.fileId = document.file_id;
      documentDetail.type = document.type;
      documentDetails.push(documentDetail);
    });
    return documentDetails;
  }

  private getIndiaBasicBusinessInformation(
    data: IndiaBasicBusinessInformationData,
  ): Business {
    let business = {} as Business;
    business.r2AId = '';
    if (data.business_name != null) {
      business.name = data.business_name;
    }
    business.PAN = data.pan;
    business.CIN = data.cin;
    business.GSTIN = data.gstin;
    business.authorizedOwnerName = data.authorized_person_name;
    business.iecCode = data.iec_code;
    business.noGSTOrIEC = data.no_gst_or_iec;
    business.llpIN = data.llp_in;
    business.documents = this.getBusinessDocumentsFromSupportDocuments(
      data.support_documents as SupportDocument[],
    );
    business.doingbusinessAs = data.doing_business_as;

    if (data.address) business.address = this.getAddress(data.address);
    if (data.billing_address)
      business.billingAddress = this.getAddress(data.billing_address);
    business.businessActivity = this.mapperForApiToFrontendBusinessActivity()[
      data.business_activity as BusinessActivityType
    ] as BusinessActivity;
    return business;
  }

  private getApiToFrontendIndiaBasicBusinessNonEditableFields(
    data: IndiaBasicBusinessInformationData,
  ): BusinessSearchNonEditablesEnum[] {
    let response: BusinessSearchNonEditablesEnum[] = [];
    if (
      data.non_editable_fields?.indexOf('CIN') != undefined &&
      data.non_editable_fields?.indexOf('CIN') > -1
    ) {
      response.push(BusinessSearchNonEditablesEnum.CIN);
    }
    if (
      data.non_editable_fields?.indexOf('GSTIN') != undefined &&
      data.non_editable_fields?.indexOf('GSTIN') > -1
    ) {
      response.push(BusinessSearchNonEditablesEnum.GSTIN);
    }
    if (
      data.non_editable_fields?.indexOf('PAN') != undefined &&
      data.non_editable_fields?.indexOf('PAN') > -1
    ) {
      response.push(BusinessSearchNonEditablesEnum.PAN);
    }
    if (
      data.non_editable_fields?.indexOf('NAME') != undefined &&
      data.non_editable_fields?.indexOf('NAME') > -1
    ) {
      response.push(BusinessSearchNonEditablesEnum.name);
    }
    if (
      data.non_editable_fields?.indexOf('AUTHORIZED_OWNER_NAME') != undefined &&
      data.non_editable_fields?.indexOf('AUTHORIZED_OWNER_NAME') > -1
    ) {
      response.push(BusinessSearchNonEditablesEnum.AuthorizedOwnerName);
    }
    return response;
  }

  private getFrontendToApiIndiaBasicBusinessNonEditableFields(
    business: Business,
  ): IndiaBasicBusinessInformationNonEditableEnum[] {
    let response: IndiaBasicBusinessInformationNonEditableEnum[] = [];
    if (
      business.nonEditables?.indexOf(BusinessSearchNonEditablesEnum.CIN) !=
        undefined &&
      business.nonEditables?.indexOf(BusinessSearchNonEditablesEnum.CIN) > -1
    ) {
      response.push('CIN');
    }
    if (
      business.nonEditables?.indexOf(BusinessSearchNonEditablesEnum.GSTIN) !=
        undefined &&
      business.nonEditables?.indexOf(BusinessSearchNonEditablesEnum.GSTIN) > -1
    ) {
      response.push('GSTIN');
    }
    if (
      business.nonEditables?.indexOf(BusinessSearchNonEditablesEnum.PAN) !=
        undefined &&
      business.nonEditables?.indexOf(BusinessSearchNonEditablesEnum.PAN) > -1
    ) {
      response.push('PAN');
    }
    if (
      business.nonEditables?.indexOf(BusinessSearchNonEditablesEnum.name) !=
        undefined &&
      business.nonEditables?.indexOf(BusinessSearchNonEditablesEnum.name) > -1
    ) {
      response.push('NAME');
    }
    if (
      business.nonEditables?.indexOf(
        BusinessSearchNonEditablesEnum.AuthorizedOwnerName,
      ) != undefined &&
      business.nonEditables?.indexOf(
        BusinessSearchNonEditablesEnum.AuthorizedOwnerName,
      ) > -1
    ) {
      response.push('AUTHORIZED_OWNER_NAME');
    }
    return response;
  }

  private getIndiaBusinessDirectorDetails(
    data: IndiaBusinessDirectorDetailsData,
  ): Business {
    let business = {} as Business;
    if (data.director_detail_list) {
      business.directors = this.getAuthorizedOwners(
        data.director_detail_list as DirectorDetails[],
      );
    }
    return business;
  }

  private getBusinessCredibilityInformation(
    data: BusinessCredibilityInformationData,
    currentBusiness: Business,
  ): Business {
    let business = {} as Business;
    if (data.business_website_url) {
      business.websiteUrl = data.business_website_url;
    }
    if (
      data.average_invoice_amount &&
      data.average_invoice_amount.amount &&
      data.average_number_of_invoices_per_month
    ) {
      business.avgInvoiceSizeInInr = data.average_invoice_amount.amount;
    }
    if (data.average_number_of_invoices_per_month) {
      business.avgNoOfInvoicesPerMonth =
        data.average_number_of_invoices_per_month;
    }

    let supporting_doc = currentBusiness.documents;
    if (data.support_documents) {
      const additionalSupportDocuments =
        this.getBusinessDocumentsFromSupportDocuments(
          data.support_documents as SupportDocument[],
        );
      if (supporting_doc == undefined) supporting_doc = [];
      supporting_doc = supporting_doc.concat(additionalSupportDocuments);
    }
    if (supporting_doc != undefined) {
      business.documents = supporting_doc;
    }

    return business;
  }

  private getUsBasicBusinessInformation(
    data: UsBasicBusinessInformationData,
  ): Business {
    let business = {} as Business;
    business.r2AId = '';
    if (data.name != null) {
      business.name = data.name;
    }

    if (data.billing_address) {
      business.address = this.getAddress(data.billing_address);
      business.billingAddress = this.getAddress(data.billing_address);
    }
    business.dateEstablished = data.date_established;
    business.name = data.name ? data.name : '';
    business.federalEIN = data.federal_ein;
    business.businessActivity = this.mapperForApiToFrontendBusinessActivity()[
      data.nature_of_business as BusinessActivityType
    ] as BusinessActivity;
    business.authorizedOfficer = {
      name: data.authorized_officer_full_name,
      designation: data.designation,
    } as AuthorizedOfficer;
    return business;
  }

  private getBusiness(
    request: PatchVerificationStepDataResponseModel,
  ): Business {
    let business = {} as Business;

    if (request.steps === undefined) return business;

    request.steps.forEach((step) => {
      if (step.verification_step_data == undefined) return;

      if ('india_basic_business_information' in step.verification_step_data) {
        Object.assign(
          business,
          this.getIndiaBasicBusinessInformation(
            step.verification_step_data.india_basic_business_information,
          ),
        );
        business.nonEditables =
          this.getApiToFrontendIndiaBasicBusinessNonEditableFields(
            step.verification_step_data.india_basic_business_information,
          );
      }
      if ('india_business_director_details' in step.verification_step_data) {
        Object.assign(
          business,
          this.getIndiaBusinessDirectorDetails(
            step.verification_step_data.india_business_director_details,
          ),
        );
      }
      if ('business_credibility_information' in step.verification_step_data) {
        Object.assign(
          business,
          this.getBusinessCredibilityInformation(
            step.verification_step_data.business_credibility_information,
            business,
          ),
        );
      }
      if ('us_basic_business_information' in step.verification_step_data) {
        Object.assign(
          business,
          this.getUsBasicBusinessInformation(
            step.verification_step_data.us_basic_business_information,
          ),
        );
      }
    });

    business.businessRegistrationType = this
      .registration_type as BusinessRegistrationType;
    (business.contactPerson = ''),
      (business.emailAddress = ''),
      (business.vendorName = ''),
      (business.requestedDate = ''),
      (business.profileImagePath = ''),
      (business.completed = false),
      (business.role = ''),
      (business.createdDate = '');

    return business;
  }

  private populateErrorsForAStep(
    step: Step,
    verificationStep: VerificationStep,
  ): IdentityVerificationStepErrors[] {
    let errors = [] as IdentityVerificationStepErrors[];
    verificationStep.step_data_error_list?.forEach((value) => {
      errors.push({
        errorCode: value.error_code,
        errorDescription: value.error_description,
      });
    });
    return errors;
  }

  private getIndiaBusinessComplianceQuestionsFlowStep(
    request: IndiaBusinessComplianceQuestionsData,
    verificationStep: VerificationStep,
  ): Step {
    let businessComplianceCheckStep = {} as Step;
    businessComplianceCheckStep.details = {
      responseLine1: request.software_for_military,
      responseLine2: request.voip_services,
    };
    businessComplianceCheckStep.stepId = 'business_compliance_check';
    businessComplianceCheckStep.state =
      verificationStep.status == 'COMPLETE' ? 'completed' : 'pending';
    businessComplianceCheckStep.action = 'required';
    businessComplianceCheckStep.errors = this.populateErrorsForAStep(
      businessComplianceCheckStep,
      verificationStep,
    );
    return businessComplianceCheckStep;
  }

  private getUsBusinessComplianceQuestionsFlowStep(
    request: UsBusinessComplianceQuestionsData,
    verificationStep: VerificationStep,
  ): Step {
    let businessComplianceCheckStep = {} as Step;
    businessComplianceCheckStep.details = {
      responseLine1: request.software_for_military,
      responseLine2: request.voip_services,
    };
    businessComplianceCheckStep.stepId = 'business_compliance_check';
    businessComplianceCheckStep.state =
      verificationStep.status == 'COMPLETE' ? 'completed' : 'pending';
    businessComplianceCheckStep.action = 'required';
    businessComplianceCheckStep.errors = this.populateErrorsForAStep(
      businessComplianceCheckStep,
      verificationStep,
    );
    return businessComplianceCheckStep;
  }

  /**
   * When the API cannot confirm a name match, it may still return
   * account_holder_name_verification_notice so onboarding can continue.
   */
  private isIndiaBusinessBankNameMismatch(
    data: IndiaBusinessBankDetailsData,
  ): boolean {
    if (data.account_holder_name_matched === true) {
      return false;
    }
    if (data.account_holder_name_matched === false) {
      return true;
    }
    const entered = data.name_as_entered?.trim() ?? '';
    const perBank = data.name_as_per_bank?.trim() ?? '';
    if (entered.length > 0 && perBank.length > 0) {
      return entered !== perBank;
    }
    return false;
  }

  private getIndiaBankDetailsFlowStep(
    request: IndiaBusinessBankDetailsData,
    verificationStep: VerificationStep,
  ): Step {
    let bankDetailsCheckStep = {} as Step;
    const notice = request.account_holder_name_verification_notice?.trim();
    const canCompleteWithVerificationNotice =
      !!notice && this.isIndiaBusinessBankNameMismatch(request);

    bankDetailsCheckStep.details = {
      accountNumber: request.account_number,
      confirmAccountNumber: request.confirm_account_number,
      ifscCode: request.ifsc_code,
      nameAsPerBank: request.name_as_per_bank,
      nameAsEntered: request.name_as_entered,
      accountHolderNameMatched: request.account_holder_name_matched,
      accountHolderNameVerificationNotice:
        request.account_holder_name_verification_notice,
    };
    bankDetailsCheckStep.stepId = 'bank_account_details_info_collection';
    bankDetailsCheckStep.state =
      verificationStep.status == 'COMPLETE' || canCompleteWithVerificationNotice
        ? 'completed'
        : 'pending';
    bankDetailsCheckStep.action = 'required';
    const stepErrors = this.populateErrorsForAStep(
      bankDetailsCheckStep,
      verificationStep,
    );
    bankDetailsCheckStep.errors = canCompleteWithVerificationNotice
      ? []
      : stepErrors;
    return bankDetailsCheckStep;
  }

  private getIndiaBasicBusinessInformationFlowStep(
    request: IndiaBasicBusinessInformationData,
    verificationStep: VerificationStep,
  ): Step {
    let step = {} as Step;
    step.details = {
      autoPopulated:
        request.auto_populated != undefined ? request.auto_populated : false,
    };
    step.stepId = 'business_activity_validation';
    step.state =
      verificationStep.status == 'COMPLETE' ? 'completed' : 'pending';
    step.action = 'required';
    step.errors = this.populateErrorsForAStep(step, verificationStep);
    return step;
  }

  private getUsBasicBusinessInformationFlowStep(
    request: UsBasicBusinessInformationData,
    verificationStep: VerificationStep,
  ): Step {
    let step = {} as Step;
    step.details = {};
    step.stepId = 'business_activity_validation';
    step.state =
      verificationStep.status == 'COMPLETE' ? 'completed' : 'pending';
    step.action = 'required';
    return step;
  }

  private getIndiaBusinessDirectorDetailsFlowStep(
    request: IndiaBusinessDirectorDetailsData,
    verificationStep: VerificationStep,
  ): Step {
    let step = {} as Step;
    step.details = {};
    step.stepId = 'director_selection';
    step.state =
      verificationStep.status == 'COMPLETE' ? 'completed' : 'pending';
    step.action = 'required';
    step.errors = this.populateErrorsForAStep(step, verificationStep);
    return step;
  }

  private getBusinessCredibilityInformationFlowStep(
    request: BusinessCredibilityInformationData,
    verificationStep: VerificationStep,
  ): Step {
    let step = {} as Step;
    step.details = {
      businessPurpose: request.business_purpose,
    };
    step.stepId = 'additional_info_collection';
    step.state =
      verificationStep.status == 'COMPLETE' ? 'completed' : 'pending';
    step.action = 'required';
    step.errors = this.populateErrorsForAStep(step, verificationStep);
    return step;
  }

  private getVendorOnboardingFlowSteps(
    request: PatchVerificationStepDataResponseModel,
  ): FlowSteps {
    let vendorOnboardingFlowSteps = {
      steps: [
        {
          stepId: 'select_business',
          state: 'completed',
          action: 'required',
          details: {
            description: '',
            autoPopulated: false,
          },
        },
      ],
    } as FlowSteps;

    if (request.steps == undefined) {
      return vendorOnboardingFlowSteps;
    }

    let someCompanyInformationFound = false;

    request.steps.forEach((step) => {
      if (step.verification_step_data == undefined) return;

      if ('india_basic_business_information' in step.verification_step_data) {
        someCompanyInformationFound =
          step.verification_step_data.india_basic_business_information
            .business_name != undefined &&
          step.verification_step_data.india_basic_business_information
            .business_name.length > 0;
        vendorOnboardingFlowSteps.steps.push(
          this.getIndiaBasicBusinessInformationFlowStep(
            step.verification_step_data.india_basic_business_information,
            step,
          ),
        );
      }

      if ('us_basic_business_information' in step.verification_step_data) {
        vendorOnboardingFlowSteps.steps.push(
          this.getUsBasicBusinessInformationFlowStep(
            step.verification_step_data.us_basic_business_information,
            step,
          ),
        );
      }

      if ('india_business_director_details' in step.verification_step_data) {
        vendorOnboardingFlowSteps.steps.push(
          this.getIndiaBusinessDirectorDetailsFlowStep(
            step.verification_step_data.india_business_director_details,
            step,
          ),
        );
      }

      if ('india_business_bank_details' in step.verification_step_data) {
        vendorOnboardingFlowSteps.steps.push(
          this.getIndiaBankDetailsFlowStep(
            step.verification_step_data.india_business_bank_details,
            step,
          ),
        );
      }

      if (
        'india_business_compliance_questions' in step.verification_step_data
      ) {
        vendorOnboardingFlowSteps.steps.push(
          this.getIndiaBusinessComplianceQuestionsFlowStep(
            step.verification_step_data.india_business_compliance_questions,
            step,
          ),
        );
      }

      if ('us_business_compliance_questions' in step.verification_step_data) {
        vendorOnboardingFlowSteps.steps.push(
          this.getUsBusinessComplianceQuestionsFlowStep(
            step.verification_step_data.us_business_compliance_questions,
            step,
          ),
        );
      }

      if ('business_credibility_information' in step.verification_step_data) {
        vendorOnboardingFlowSteps.steps.push(
          this.getBusinessCredibilityInformationFlowStep(
            step.verification_step_data.business_credibility_information,
            step,
          ),
        );
      }
    });

    if (someCompanyInformationFound) {
      vendorOnboardingFlowSteps.steps[0].details['autoPopulated'] = true;
    }

    return vendorOnboardingFlowSteps;
  }

  private getBusinessOnboardingResponse(
    request: PatchVerificationStepDataResponseModel,
  ): BusinessOnboardingResponse {
    let returnValue: BusinessOnboardingResponse = {
      onboardingStatus: 'pending',
      business: this.getBusiness(request),
      // @todo chaitanya
      flowSteps: this.getVendorOnboardingFlowSteps(request),
      context: 'new_business_onboarding',
      onboardingId: request.verification_id,
    };
    switch (request.status) {
      case 'APPROVED':
        returnValue.onboardingStatus = 'completed';
        break;
      case 'PENDING':
        returnValue.onboardingStatus = 'pending';
        break;
      case 'IN_PROGRESS':
        returnValue.onboardingStatus = 'pending';
        break;
      case 'UNDER_REVIEW':
        returnValue.onboardingStatus = 'manual_review';
        break;
    }
    return returnValue;
  }

  private getSupportDocumentList(
    uploadedDocuments: DocumentDetails[],
  ): Array<SupportDocument> {
    let supportDocumentList = [] as Array<SupportDocument>;

    uploadedDocuments.forEach((document) => {
      let supportDocument = {} as SupportDocument;
      supportDocument.name = document.name;
      supportDocument.type = document.type;
      supportDocument.file_id = document.fileId;
      supportDocumentList.push(supportDocument);
    });

    return supportDocumentList;
  }

  async submitBusinessDetails(
    request: CreateBusinessRequest,
    verificationId: string,
  ): Promise<BusinessOnboardingResponse> {
    console.log(request);
    // pincode is missing from request params

    let stepId: VerificationStepName;
    let patchVerificationStepDataRequestContent: PatchVerificationStepDataRequestContent;

    if (request.business.country.name === 'India') {
      stepId = 'IN_BASIC_BUSINESS_INFORMATION';
      let pinCode = '';
      if (
        request?.business?.address !== undefined &&
        'pinCode' in request.business.address.specificDetails
      ) {
        pinCode = request.business.address.specificDetails.pinCode;
      }
      let billingAddressPinCode = '';
      if (
        request?.business?.billingAddress !== undefined &&
        'pinCode' in request.business.billingAddress.specificDetails
      ) {
        billingAddressPinCode =
          request.business.billingAddress.specificDetails.pinCode;
      }
      patchVerificationStepDataRequestContent = {
        operation: 'SUBMIT',
        step_data: {
          india_basic_business_information: {
            pan: request.business.PAN,
            cin: request.business.CIN,
            gstin: request.business.GSTIN,
            iec_code: request.business.iecCode,
            no_gst_or_iec: request.business.noGSTOrIEC,
            llp_in: request.business.llpIN,
            business_name: request.business.name,
            authorized_person_name: request.business.authorizedOwnerName,
            non_editable_fields:
              this.getFrontendToApiIndiaBasicBusinessNonEditableFields(
                request.business,
              ),
            address: {
              id: '',
              address_line1: request.business.address
                ? request.business.address.street
                : '',
              address_line2: '',
              country_code: request.business.country.code,
              state: request.business.address
                ? request.business.address.state
                : '',
              city: request.business.address
                ? request.business.address.city
                : '',
              pin_code: pinCode,
            },
            billing_address: {
              id: '',
              address_line1: request.business.billingAddress
                ? request.business.billingAddress.street
                : '',
              address_line2: '',
              country_code: request.business.country.code,
              state: request.business.billingAddress
                ? request.business.billingAddress.state
                : '',
              city: request.business.billingAddress
                ? request.business.billingAddress.city
                : '',
              pin_code: billingAddressPinCode,
            },
            business_activity: this.mapperForFrontendToApiBusinessActivity()[
              request.business.businessActivity as BusinessActivity
            ] as BusinessActivityType,
            doing_business_as: request.business.doingbusinessAs,
            support_documents: this.getSupportDocumentList(
              request.business.documents || [],
            ),
          },
        },
      };
    } else {
      // todo
      stepId = 'US_BASIC_BUSINESS_INFORMATION';
      let billingAddressPinCode = '';
      if (
        request.business.billingAddress !== undefined &&
        'zipCode' in request.business.billingAddress.specificDetails
      ) {
        billingAddressPinCode =
          request.business.billingAddress.specificDetails.zipCode;
      }
      patchVerificationStepDataRequestContent = {
        operation: 'SUBMIT',
        step_data: {
          us_basic_business_information: {
            name: request.business.name,
            billing_address: {
              id: '',
              address_line1: request.business.billingAddress
                ? request.business.billingAddress.street
                : '',
              address_line2: '',
              country_code: request.business.country.code,
              state: request.business.billingAddress
                ? request.business.billingAddress.state
                : '',
              city: request.business.billingAddress
                ? request.business.billingAddress.city
                : '',
              pin_code: billingAddressPinCode,
            },
            federal_ein: request.business.federalEIN,
            date_established: request.business.dateEstablished,
            nature_of_business: this.mapperForFrontendToApiBusinessActivity()[
              request.business.businessActivity as BusinessActivity
            ] as BusinessActivityType,
            authorized_officer_full_name:
              request?.business?.authorizedOfficer?.name,
            designation: request?.business?.authorizedOfficer?.designation,
            verification_document_id: '',
          },
        },
      };
    }

    let dataFromResponse = await this.openapiService.patchVerificationStepData(
      verificationId,
      stepId,
      patchVerificationStepDataRequestContent,
    );

    if (dataFromResponse.data.steps === undefined) {
      throw new Error('API RESPONSE ERROR');
    }

    return this.getBusinessOnboardingResponse(dataFromResponse.data);
  }

  private getDirectorDetailsList(
    directorList: AuthorizedOwner[],
  ): Array<DirectorDetails> {
    return directorList.map((director) => ({
      id: director.id,
      name: director.name,
      pan: director.PAN,
      ubo: director.roles.indexOf(Role.UBO) > -1,
      senior_management: director.roles.indexOf(Role.SrManager) > -1,
      is_manual: director.isManual,
      update_enabled: director.updateEnabled,
      is_aadhar_verified: undefined,
      aadhar_verification_details: {
        aadhar_number: '',
        verification_id: '',
      },
    })) as Array<DirectorDetails>;
  }

  async addAuthorizedOwner(
    owners: AuthorizedOwner[],
    verificationId: string,
  ): Promise<BusinessOnboardingResponse> {
    let stepId: VerificationStepName = 'IN_BUSINESS_DIRECTOR_DETAILS';
    let patchVerificationStepDataRequestContent: PatchVerificationStepDataRequestContent =
      {
        operation: 'SAVE',
        step_data: {
          india_business_director_details: {
            // director_detail_list: [
            //   /*{
            //      name: "rocky",
            //      pan: "bbbbbbbbb",
            //      ubo: true,
            //      senior_management: true,
            //      is_aadhar_verified: true,
            //      aadhar_verification_details: {
            //          aadhar_number: "12121212",
            //          verification_id: "qwqwqwqw"
            //      }
            //  } */
            // ]
            director_detail_list: this.getDirectorDetailsList(owners),
          },
        },
      };
    let dataFromResponse = await this.openapiService.patchVerificationStepData(
      verificationId,
      stepId,
      patchVerificationStepDataRequestContent,
    );

    if (dataFromResponse.data.steps === undefined) {
      throw new Error('API RESPONSE ERROR');
    }

    return this.getBusinessOnboardingResponse(dataFromResponse.data);
  }

  async updateAuthorizedOwner(
    owners: AuthorizedOwner[],
    verificationId: string,
  ): Promise<BusinessOnboardingResponse> {
    console.log('Updating directors', owners);

    let stepId: VerificationStepName = 'IN_BUSINESS_DIRECTOR_DETAILS';
    let patchVerificationStepDataRequestContent: PatchVerificationStepDataRequestContent =
      {
        operation: 'SAVE',
        step_data: {
          india_business_director_details: {
            director_detail_list: this.getDirectorDetailsList(owners),
          },
        },
      };
    let dataFromResponse = await this.openapiService.patchVerificationStepData(
      verificationId,
      stepId,
      patchVerificationStepDataRequestContent,
    );

    if (dataFromResponse.data.steps === undefined) {
      throw new Error('API RESPONSE ERROR');
    }

    return this.getBusinessOnboardingResponse(dataFromResponse.data);

    /*     let directorsList = mockBusinesses[0].directors
      ? mockBusinesses[0].directors
      : [];
    console.log('directorsList', directorsList);
    const element = directorsList.filter((x) => x.id == owner.id);
    const index: number = directorsList.indexOf(element[0]);
    directorsList[index] = owner; */
    // return Promise.resolve({
    //   business: mockBusinesses[0],
    //   flowSteps: mockVendorOnboardingFlowStepsAfterDirectorSelection,
    //   context: 'new_business_onboarding',
    //   onboardingStatus: 'pending',
    // });
  }

  async deleteAuthorizedOwner(
    owners: AuthorizedOwner[],
    verificationId: string,
  ): Promise<BusinessOnboardingResponse> {
    let stepId: VerificationStepName = 'IN_BUSINESS_DIRECTOR_DETAILS';
    let patchVerificationStepDataRequestContent: PatchVerificationStepDataRequestContent =
      {
        operation: 'SAVE',
        step_data: {
          india_business_director_details: {
            director_detail_list: this.getDirectorDetailsList(owners),
          },
        },
      };

    let dataFromResponse = await this.openapiService.patchVerificationStepData(
      verificationId,
      stepId,
      patchVerificationStepDataRequestContent,
    );

    if (dataFromResponse.data.steps === undefined) {
      throw new Error('API RESPONSE ERROR');
    }

    return this.getBusinessOnboardingResponse(dataFromResponse.data);
  }

  async submitAuthorizedOwners(
    owners: AuthorizedOwner[],
    verificationId: string,
  ): Promise<BusinessOnboardingResponse> {
    let stepId: VerificationStepName = 'IN_BUSINESS_DIRECTOR_DETAILS';
    let patchVerificationStepDataRequestContent: PatchVerificationStepDataRequestContent =
      {
        operation: 'SUBMIT',
        step_data: {
          india_business_director_details: {
            director_detail_list: this.getDirectorDetailsList(owners),
          },
        },
      };

    let dataFromResponse = await this.openapiService.patchVerificationStepData(
      verificationId,
      stepId,
      patchVerificationStepDataRequestContent,
    );

    if (dataFromResponse.data.steps === undefined) {
      throw new Error('API RESPONSE ERROR');
    }

    return this.getBusinessOnboardingResponse(dataFromResponse.data);
    //mockBusinesses[0].directors = owners;
  }

  private inInputForBusinessResponsibilities(
    request: BusinessResponsibilitiesRequest,
    verificationId: string,
  ): PatchVerificationStepDataRequestContent {
    let voipServices;
    let softwareForMilitary;
    request.responsibilities.forEach((value: Responsibility, key: number) => {
      if (value.type == 'voip_services') {
        voipServices = value.answers[0];
      }
      if (value.type == 'software_for_military') {
        softwareForMilitary = value.answers[0];
      }
    });
    let patchVerificationStepDataRequestContent: PatchVerificationStepDataRequestContent =
      {
        operation: 'SUBMIT',
        step_data: {
          india_business_compliance_questions: {
            voip_services: voipServices,
            software_for_military: softwareForMilitary,
          },
        },
      };
    return patchVerificationStepDataRequestContent;
  }

  private usInputForBusinessResponsibilities(
    request: BusinessResponsibilitiesRequest,
    verificationId: string,
  ): PatchVerificationStepDataRequestContent {
    let voipServices;
    let softwareForMilitary;
    request.responsibilities.forEach((value: Responsibility, key: number) => {
      if (value.type == 'voip_services') {
        voipServices = value.answers[0];
      }
      if (value.type == 'software_for_military') {
        softwareForMilitary = value.answers[0];
      }
    });
    let patchVerificationStepDataRequestContent: PatchVerificationStepDataRequestContent =
      {
        operation: 'SUBMIT',
        step_data: {
          us_business_compliance_questions: {
            voip_services: voipServices,
            software_for_military: softwareForMilitary,
          },
        },
      };
    return patchVerificationStepDataRequestContent;
  }

  async updateBusinessResponsibilities(
    request: BusinessResponsibilitiesRequest,
    verificationId: string,
  ): Promise<BusinessOnboardingResponse> {
    console.log('Adding additional information', request);

    let stepId: VerificationStepName;
    let patchVerificationStepDataRequestContent: PatchVerificationStepDataRequestContent;
    if (request.countryCode.code === 'US') {
      stepId = 'US_BUSINESS_COMPLIANCE_QUESTIONS';
      patchVerificationStepDataRequestContent =
        this.usInputForBusinessResponsibilities(request, verificationId);
    } else {
      stepId = 'IN_BUSINESS_COMPLIANCE_QUESTIONS';
      patchVerificationStepDataRequestContent =
        this.inInputForBusinessResponsibilities(request, verificationId);
    }

    let dataFromResponse = await this.openapiService.patchVerificationStepData(
      verificationId,
      stepId,
      patchVerificationStepDataRequestContent,
    );

    if (dataFromResponse.data.steps === undefined) {
      throw new Error('API RESPONSE ERROR');
    }

    return this.getBusinessOnboardingResponse(
      dataFromResponse.data,
    ) as BusinessOnboardingResponse;
  }

  async addAdditionalInfo(
    request: AdditionalInfoRequest,
    verificationId: string,
  ): Promise<BusinessOnboardingResponse> {
    console.log('Adding additional information', request);

    let stepId: VerificationStepName = 'BUSINESS_CREDIBILITY_INFORMATION';

    let patchVerificationStepDataRequestContent: PatchVerificationStepDataRequestContent =
      {
        operation: 'SUBMIT',
        step_data: {
          business_credibility_information: {
            business_website_url: request.websiteUrl,
            online_presence_url: request.onlineShopLink,
            average_invoice_amount: {
              amount: request.avgInvoiceSizeInInr ?? 0,
              currency: 'INR',
            },
            business_purpose: request.businessPurpose,
            average_number_of_invoices_per_month:
              request.avgNoOfInvoicesPerMonth,
            support_documents: this.getSupportDocumentList(
              request?.supportDocuments || [],
            ),
            purpose_code: (request.purposeCodes || []).map((code) => ({
              purpose_code: code,
            })),
          },
        },
      };

    let dataFromResponse = await this.openapiService.patchVerificationStepData(
      verificationId,
      stepId,
      patchVerificationStepDataRequestContent,
    );

    if (dataFromResponse.data.steps === undefined) {
      throw new Error('API RESPONSE ERROR');
    }

    return this.getBusinessOnboardingResponse(
      dataFromResponse.data,
    ) as BusinessOnboardingResponse;

    /*return Promise.resolve(
       {
      business: mockBusinesses[0],
      flowSteps: mockVendorOnboardingFlowStepsAfterAdditonalInfoSelection,
      context: 'new_business_onboarding',
      onboardingStatus: 'pending',
    })*/
  }

  async getPurposeCodes(customerId: string): Promise<{
    success: boolean;
    data: any[];
    error: string;
  }> {
    try {
      const response = await this.openapiService.getPurposeCodes(customerId);
      return response;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        data: [],
      };
    }
  }

  async submitAndVerify(
    verificationId: string,
  ): Promise<BusinessOnboardingResponse> {
    console.log('Verifying customer', verificationId);
    let dataFromResponse =
      await this.openapiService.patchVerificationFlowSubmission(verificationId);
    if (dataFromResponse === undefined || dataFromResponse.data === undefined) {
      throw new Error('Api response error');
    }

    if (dataFromResponse.data.status != 'UNDER_REVIEW') {
      throw new Error(
        'Please ensure that all the details have been entered properly. If all the details have been entered properly, please contact us at ' +
          environment.support_email,
      );
    }

    return this.getBusinessOnboardingResponse(
      dataFromResponse.data,
    ) as BusinessOnboardingResponse;
  }

  async inviteBusiness(request: InviteBusinessRequest): Promise<boolean> {
    console.log(environment.organization_id);
    let createPartnerRequestContent: CreatePartnerRequestContent;
    const userResponse = await this.openapiService.getOrCreateUser();
    const owningCustomerId = userResponse.data?.current_context_customer_id;
    if (!owningCustomerId) {
      throw new Error('No owning customer available for partner creation');
    }
    const countryCode: CountryIsoCode = request.partialBusiness?.country
      ?.code as CountryIsoCode;
    console.log('request.partialBusiness?.r2AId', request);
    let specificDetails;

    if (request.partialBusiness?.r2AId) {
      createPartnerRequestContent = {
        associative_customer_id: request.partialBusiness?.r2AId,
      };
    } else {
      createPartnerRequestContent = {
        business_name: request.partialBusiness?.clientName,
        address_line1: request.partialBusiness?.address?.street,
        address_line2: request.partialBusiness?.address?.street,
        country_code: countryCode,
        state: request.partialBusiness?.address?.state,
        city: request.partialBusiness?.address?.city,
        contact_person_name: request.partialBusiness?.contactPerson,
        service_provided: this.mapperForFrontendToApiBusinessActivity()[
          request.partialBusiness?.businessActivity as BusinessActivity
        ] as BusinessActivityType,
        contact_email: request.partialBusiness?.emailAddress,
        contact_phone_number: request.partialBusiness?.phoneNumber,
        contact_person_designation: '',
        company_type: this.mapperForFrontendToApiCompanyRegistrationType()[
          request.partialBusiness?.companyType as CompanyRegistrationType
        ] as CompanyType,
      };

      if (
        request?.partialBusiness?.address !== undefined &&
        'pinCode' in request.partialBusiness.address.specificDetails
      ) {
        createPartnerRequestContent['pin_code'] =
          request.partialBusiness.address.specificDetails.pinCode;
      }
      if (
        request?.partialBusiness?.address !== undefined &&
        'zipCode' in request.partialBusiness.address.specificDetails
      ) {
        createPartnerRequestContent['pin_code'] =
          request.partialBusiness.address.specificDetails.zipCode;
      }
    }

    let getPartnerApiResponse = await this.openapiService.createPartner(
      owningCustomerId,
      createPartnerRequestContent,
    );

    return Promise.resolve(getPartnerApiResponse.data.id != undefined);
  }
  getPartnerAccountDepartment(
    accountDepartment: AccountDepartment,
  ): PartnerAccountDetails {
    let term: Term;
    if (accountDepartment.dueDateFrequency === 'Days') term = 'DAYS';
    else term = 'MONTHS';

    const accountDetails: PartnerAccountDetails = {
      contact_person_name: accountDepartment.contactPersonName,
      contact_person_email: accountDepartment.contactPersonEmail,
      contact_person_ph_no: accountDepartment.contactPersonPhNo,
      client_code: accountDepartment.clientCode,
      last_invoice_number: accountDepartment.lastInvoiceNumber,
      due_date_count: accountDepartment.dueDateCount,
      due_date_frequency: term,
    };
    return accountDetails;
  }

  async updateBusiness(data: AccountDepartment, id: string): Promise<boolean> {
    let updatePartnerRequestContent: UpdatePartnerRequestContent = {
      partner_account_details: this.getPartnerAccountDepartment(data),
    };
    const customer: Profile = this.sharedProfileData.getUserProfile();
    let getPartnerApiResponse = await this.openapiService.updatePartner(
      { id: id },
      customer.businessId,
      updatePartnerRequestContent,
    );
    return Promise.resolve(getPartnerApiResponse.success);
  }

  async addBankAccountDetails(
    request: BankAccountDetailsInfoRequest,
    verificationId: string,
  ): Promise<BusinessOnboardingResponse> {
    console.log('Adding bank account details information', request);

    let stepId: VerificationStepName = 'IN_BUSINESS_BANK_DETAILS';

    let patchVerificationStepDataRequestContent: PatchVerificationStepDataRequestContent =
      {
        operation: 'SUBMIT',
        step_data: {
          india_business_bank_details: {
            account_number: request.accountNumber,
            confirm_account_number: request.confirmAccountNumber,
            ifsc_code: request.ifscCode,
          },
        },
      };
    let dataFromResponse = await this.openapiService.patchVerificationStepData(
      verificationId,
      stepId,
      patchVerificationStepDataRequestContent,
    );
    console.log(dataFromResponse);

    if (dataFromResponse.data.steps === undefined) {
      throw new Error('API RESPONSE ERROR');
    }

    for (const step of dataFromResponse.data.steps) {
      if (
        step.name === 'IN_BUSINESS_BANK_DETAILS' &&
        step.verification_step_data &&
        'india_business_bank_details' in step.verification_step_data
      ) {
        const bd = step.verification_step_data.india_business_bank_details;
        console.log('IN_BUSINESS_BANK_DETAILS (API)', {
          account_number: bd.account_number,
          confirm_account_number: bd.confirm_account_number,
          ifsc_code: bd.ifsc_code,
          name_as_per_bank: bd.name_as_per_bank,
          name_as_entered: bd.name_as_entered,
          account_holder_name_matched: bd.account_holder_name_matched,
        });
        break;
      }
    }

    return this.getBusinessOnboardingResponse(
      dataFromResponse.data,
    ) as BusinessOnboardingResponse;
  }

  async getIdentityVerifications(): Promise<OnboardingQueueItem[]> {
    const response = await this.openapiService.getIdentityVerifications();

    if (!response.success || !response.data) {
      throw new Error(
        response.error || 'Failed to fetch identity verifications',
      );
    }

    this.identityVerificationSummariesCache =
      response.data.identity_verifications ?? [];

    return this.identityVerificationSummariesCache.map((item) =>
      this.mapToOnboardingQueueItem(item),
    );
  }

  async getIdentityVerificationDetail(
    identityVerificationId: string,
  ): Promise<OnboardingBusinessDetail | null> {
    if (!this.identityVerificationSummariesCache.length) {
      await this.getIdentityVerifications();
    }

    const summary = this.identityVerificationSummariesCache.find(
      (item) => item.identity_verification_id === identityVerificationId,
    );

    if (!summary) {
      return null;
    }

    return this.mapToOnboardingBusinessDetail(summary);
  }

  async submitIdentityVerificationQueryDocument(
    request: SubmitIdentityVerificationQueryDocumentRequest,
  ): Promise<SubmitIdentityVerificationQueryDocumentResponse> {
    const response =
      await this.openapiService.patchIdentityVerificationQueryDocuments(
        request.identityVerificationId,
        {
          query_id: request.queryId,
          file_id: request.fileId,
          purpose: request.purpose,
          ...(request.linkedId ? { linked_id: request.linkedId } : {}),
        },
      );

    if (!response.success || !response.data) {
      throw new Error(
        response.error ||
          'Failed to submit identity verification query document',
      );
    }

    return {
      verificationId: response.data.verification_id,
      queryId: response.data.query_id,
      fileId: response.data.file_id,
      status: response.data.status,
    };
  }

  private mapToOnboardingQueueItem(
    item: IdentityVerificationSummary,
  ): OnboardingQueueItem {
    return {
      id: item.identity_verification_id,
      organizationName: item.business_name?.trim() || '',
      email: '',
      xflowId: item.customer_id,
      status: mapVerificationStatus(item.identity_verification_status),
      submittedDate: item.submitted_at
        ? new Date(item.submitted_at)
        : new Date(),
    };
  }

  private mapToOnboardingBusinessDetail(
    item: IdentityVerificationSummary,
  ): OnboardingBusinessDetail {
    const queueItem = this.mapToOnboardingQueueItem(item);

    return {
      id: queueItem.id,
      customerId: item.customer_id,
      referenceId: item.customer_id,
      organizationName: queueItem.organizationName,
      status: queueItem.status,
      submittedDate: queueItem.submittedDate,
      documents: [],
      queries: this.mapOnboardingVerificationQueries(item.queries),
    };
  }

  private mapOnboardingVerificationQueries(
    queries: IdentityVerificationQuery[] = [],
  ): OnboardingVerificationQuery[] {
    return queries.map((query) => this.mapOnboardingVerificationQuery(query));
  }

  private mapOnboardingVerificationQuery(
    query: IdentityVerificationQuery,
  ): OnboardingVerificationQuery {
    const systemMessage = query.system_message as {
      code?: string;
      message?: string;
      linked_id?: string;
    };

    return {
      queryId: query.query_id,
      status: query.status,
      title: systemMessage.code ?? 'Verification Required',
      description:
        systemMessage.message ??
        'Please upload the required document to continue.',
      linkedId: systemMessage.linked_id ?? query.linked_id ?? undefined,
    };
  }
}
